import { useState, useEffect } from 'react';

const navLinks = [
  { label: 'Home', href: '#home' },
  { label: 'Services', href: '#services' },
  { label: 'About', href: '#about' },
  { label: 'Contact', href: '#contact' },
];

// AI Neural Logo with hidden "H" - brain/circuit hybrid design
function LogoIcon({ className = '' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      {/* Background - deep navy */}
      <circle cx="24" cy="24" r="24" fill="#0A1F44" />
      
      {/* Outer neural ring */}
      <circle cx="24" cy="24" r="20" stroke="#0EA5E9" strokeWidth="0.5" strokeOpacity="0.4" fill="none" />
      
      {/* Hidden "H" formed by neural pathways - left and right hemispheres connected */}
      {/* Left hemisphere nodes (forming left vertical of H) */}
      <circle cx="14" cy="16" r="2.5" fill="white" />
      <circle cx="14" cy="24" r="2.5" fill="#0EA5E9" />
      <circle cx="14" cy="32" r="2.5" fill="white" />
      
      {/* Right hemisphere nodes (forming right vertical of H) */}
      <circle cx="34" cy="16" r="2.5" fill="white" />
      <circle cx="34" cy="24" r="2.5" fill="#0EA5E9" />
      <circle cx="34" cy="32" r="2.5" fill="white" />
      
      {/* Bridge nodes - forming horizontal bar of H */}
      <circle cx="20" cy="24" r="2" fill="#38BDF8" />
      <circle cx="28" cy="24" r="2" fill="#38BDF8" />
      
      {/* Central AI core - glowing brain center */}
      <circle cx="24" cy="24" r="4" fill="#0369A1">
        <animate attributeName="opacity" values="1;0.7;1" dur="2s" repeatCount="indefinite" />
      </circle>
      <circle cx="24" cy="24" r="2" fill="white" />
      
      {/* Neural connections - left side pathways */}
      <path d="M14 16 L14 24" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M14 24 L14 32" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M14 24 L20 24" stroke="#38BDF8" strokeWidth="1.5" strokeLinecap="round" />
      
      {/* Neural connections - right side pathways */}
      <path d="M34 16 L34 24" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M34 24 L34 32" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M28 24 L34 24" stroke="#38BDF8" strokeWidth="1.5" strokeLinecap="round" />
      
      {/* Cross-hemisphere bridge (the H horizontal bar) */}
      <path d="M20 24 L28 24" stroke="white" strokeWidth="2" strokeLinecap="round" />
      
      {/* Diagonal neural connections - AI synapse style */}
      <path d="M14 16 L20 24" stroke="#0EA5E9" strokeWidth="0.8" strokeOpacity="0.6" strokeLinecap="round" />
      <path d="M14 32 L20 24" stroke="#0EA5E9" strokeWidth="0.8" strokeOpacity="0.6" strokeLinecap="round" />
      <path d="M34 16 L28 24" stroke="#0EA5E9" strokeWidth="0.8" strokeOpacity="0.6" strokeLinecap="round" />
      <path d="M34 32 L28 24" stroke="#0EA5E9" strokeWidth="0.8" strokeOpacity="0.6" strokeLinecap="round" />
      
      {/* Top and bottom extension nodes - brain lobe feel */}
      <circle cx="24" cy="10" r="1.5" fill="#0EA5E9" fillOpacity="0.7" />
      <circle cx="24" cy="38" r="1.5" fill="#0EA5E9" fillOpacity="0.7" />
      <path d="M24 10 L24 16" stroke="#3B82F6" strokeWidth="0.8" strokeOpacity="0.5" strokeLinecap="round" />
      <path d="M24 32 L24 38" stroke="#3B82F6" strokeWidth="0.8" strokeOpacity="0.5" strokeLinecap="round" />
      
      {/* Corner satellite nodes - data flow indicators */}
      <circle cx="8" cy="24" r="1" fill="white" fillOpacity="0.5" />
      <circle cx="40" cy="24" r="1" fill="white" fillOpacity="0.5" />
      <circle cx="24" cy="4" r="1" fill="white" fillOpacity="0.5" />
      <circle cx="24" cy="44" r="1" fill="white" fillOpacity="0.5" />
    </svg>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (href: string) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-[#0A1F44]/95 backdrop-blur-xl shadow-lg h-[70px]'
          : 'bg-transparent h-[80px]'
      }`}
    >
      <div className="w-full px-4 sm:px-6 lg:px-10 h-full flex items-center justify-between gap-4">
        {/* Logo — always flex-shrink-0 so it never squishes */}
        <a
          href="#home"
          onClick={(e) => { e.preventDefault(); scrollTo('#home'); }}
          className="flex items-center gap-2 cursor-pointer group flex-shrink-0"
        >
          <LogoIcon className="h-9 w-9 sm:h-10 sm:w-10 transition-transform duration-300 group-hover:scale-105" />
          <span className="font-bold tracking-wide text-white whitespace-nowrap text-sm sm:text-base lg:text-[17px]">
            nexaur<span style={{ color: '#EAB308' }}>A</span> <span style={{ color: '#EAB308' }}>I</span>nfinity
          </span>
        </a>

        {/* Desktop Nav — flex-shrink-0 so it never wraps; hidden below md */}
        <nav className="hidden md:flex items-center gap-4 lg:gap-7 xl:gap-8 flex-shrink-0">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={(e) => { e.preventDefault(); scrollTo(link.href); }}
              className={`relative text-[14px] lg:text-[15px] font-medium cursor-pointer transition-colors duration-300 group whitespace-nowrap ${
                scrolled ? 'text-white/80 hover:text-white' : 'text-white/90 hover:text-white'
              }`}
            >
              {link.label}
              <span className="absolute bottom-[-4px] left-0 w-0 h-[2px] bg-[#38BDF8] rounded-full transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        {/* CTA Button — flex-shrink-0, hidden below md */}
        <a
          href="#contact"
          onClick={(e) => { e.preventDefault(); scrollTo('#contact'); }}
          className={`hidden md:block whitespace-nowrap flex-shrink-0 px-4 lg:px-6 py-2.5 rounded-lg text-[13px] lg:text-[14px] font-semibold transition-all duration-300 cursor-pointer ${
            scrolled
              ? 'bg-[#0369A1] text-white hover:bg-[#075985]'
              : 'border-2 border-white/40 text-white hover:bg-white/10 backdrop-blur-sm'
          }`}
        >
          Transform Your Business
        </a>

        {/* Hamburger — only below md */}
        <button
          className="md:hidden w-10 h-10 flex flex-col items-center justify-center gap-1.5 cursor-pointer flex-shrink-0"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <span className={`block w-6 h-0.5 bg-white transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} />
          <span className={`block w-6 h-0.5 bg-white transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`} />
          <span className={`block w-6 h-0.5 bg-white transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden fixed top-[70px] left-0 right-0 bottom-0 bg-[#0A1F44]/98 backdrop-blur-xl transition-all duration-300 overflow-y-auto ${
          menuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
        style={{ maxHeight: 'calc(100vh - 70px)' }}
      >
        <nav className="flex flex-col items-center gap-6 px-6 py-8 min-h-full">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={(e) => { e.preventDefault(); scrollTo(link.href); }}
              className="text-white/90 hover:text-white text-lg font-medium cursor-pointer transition-colors duration-200 w-full text-center py-2"
            >
              {link.label}
            </a>
          ))}
          <a
            href="#contact"
            onClick={(e) => { e.preventDefault(); scrollTo('#contact'); }}
            className="w-full max-w-xs text-center bg-[#0369A1] text-white px-6 py-4 rounded-lg font-semibold cursor-pointer whitespace-nowrap mt-4 text-base"
          >
            Transform Your Business
          </a>
        </nav>
      </div>
    </header>
  );
}
